/**
 * 관계 그래프 캔버스 — sigma.js + graphology (DESIGN §8.1).
 *
 * **표시만 한다.** 데이터·임계·드릴다운 상태는 페이지가 정하고 여기로 내려온다.
 * 캔버스가 스스로 가져오면 화면의 점 개수와 표의 행 수가 어긋나는 순간이 생긴다.
 *
 * 지키는 것 넷:
 *
 * 1. **좌표를 다시 계산하지 않는다.** 서버가 준 x·y 를 그대로 꽂는다. 클라이언트에서
 *    ForceAtlas2 를 돌리면 같은 빌드인데 새로고침할 때마다 무리의 위치가 바뀐다.
 * 2. **hover 하이라이트는 React 를 거치지 않는다.** 이웃 집합을 ref 에 넣고 sigma 의
 *    reducer + `refresh({skipIndexation:true})` 로 다시 칠한다 — 노드 146·엣지 3,777
 *    을 React 상태로 다시 렌더하면 100ms 예산을 지킬 수 없다. 곁의 설명 문구만
 *    상태로 갱신한다 (호버당 한 번이라 비용이 없다).
 * 3. **고립 노드는 여기 오지 않는다.** ForceAtlas2 가 엣지 없는 노드를 군집 밖
 *    수천 단위 거리로 밀어내므로 (실측 연결 노드 반경 ≤551 vs 고립 3,108~4,093)
 *    전체 범위로 화면을 맞추면 본 군집이 점 하나가 된다. 페이지가 걸러 목록으로 낸다.
 * 4. **점 색은 단일 계열이다.** 색은 정체성이지 상태가 아니다 — 상태색(up/down/na)을
 *    쓰면 파란 노드가 "정상", 붉은 노드가 "고장"으로 읽힌다. 신뢰도는 색이 아니라
 *    테두리 모양(실선/파선/점선)으로 말한다 (§8.6 시그니처 3).
 */

import Graph from 'graphology'
import { useEffect, useMemo, useRef, useState } from 'react'
import Sigma from 'sigma'

import { rgb } from '@/lib/bins'
import { cssVar } from '@/lib/cssvar'
import { int, markSize, pct } from '@/lib/fmt'
import { TRUST_MARKS, trustDash, type GraphEdge, type GraphNode } from './useGraph'
import './GraphView.css'

const TAU = Math.PI * 2

/**
 * 점 지름 ∝ √n (§8.6.3 보조 규칙). 하한 3px 은 "충전기 1기짜리도 클릭할 수 있어야
 * 한다" 는 요구에서 온다 — 대신 2,400기 아래는 서로 같은 크기로 보이므로 정확한
 * 값은 표에서 읽는다.
 */
function sizeOf(n: number): number {
  return markSize(n, 3, 18, 0.061)
}

/** 굵기 ∝ |가중치|. 부호는 굵기에 넣지 않는다 — 음의 상관도 "센 관계"다 */
function widthOf(weight: number): number {
  const w = Math.min(1, Math.abs(weight))
  return 0.4 + Math.max(0, (w - 0.5) / 0.5) * 2.2
}

interface NodeAttrs {
  x: number
  y: number
  size: number
  label: string
  color: string
  zIndex: number
  nodeId: string
  coverage: number | null
  nChargers: number
  availability: number | null
}

interface EdgeAttrs {
  size: number
  color: string
  edgeType: string
  weight: number
  zIndex: number
}

interface Hovered {
  id: string
  label: string
  nChargers: number
  availability: number | null
  coverage: number | null
  nNeighbors: number
}

interface Props {
  /** 연결 노드만 (고립은 페이지가 목록으로 뺀다) */
  nodes: GraphNode[]
  edges: GraphEdge[]
  /** 드릴다운 루트 — 이중 링으로 "여기서 들어왔다"를 표시한다 */
  focus: string | null
  height: number
  onDrill: (nodeId: string) => void
}

export function GraphView({ nodes, edges, focus, height, onDrill }: Props) {
  const stageRef = useRef<HTMLDivElement | null>(null)
  const [hovered, setHovered] = useState<Hovered | null>(null)

  // 하이라이트 경로는 ref 로만 움직인다 (위 머리말 2)
  const hoverRef = useRef<string | null>(null)
  const nearNodesRef = useRef<Set<string>>(new Set())
  const nearEdgesRef = useRef<Set<string>>(new Set())
  const focusRef = useRef<string | null>(focus)
  const drillRef = useRef(onDrill)

  useEffect(() => {
    drillRef.current = onDrill
  }, [onDrill])

  const graph = useMemo(() => {
    const g = new Graph<NodeAttrs, EdgeAttrs>({ type: 'undirected' })
    const nodeColor = cssVar('--c-cat-1', '#0066cc')
    const [r1, g1, b1] = rgb(cssVar('--c-cat-1', '#0066cc'))
    const [r2, g2, b2] = rgb(cssVar('--c-cat-2', '#a35200'))
    // 엣지 3,777 개를 불투명하게 그리면 노드가 실 뭉치에 묻힌다. 낮은 알파로
    // 겹칠수록 진해지게 두면 밀도 자체가 정보가 되고, 읽어야 할 점과 테두리가 위로 뜬다
    const simColor = `rgba(${r1},${g1},${b1},0.06)`
    const corrColor = `rgba(${r2},${g2},${b2},0.14)`

    for (const n of nodes) {
      if (n.x == null || n.y == null) continue
      g.addNode(n.node_id, {
        x: n.x,
        y: n.y,
        size: sizeOf(n.n_chargers),
        label: n.label,
        color: nodeColor,
        zIndex: 0,
        nodeId: n.node_id,
        coverage: n.coverage,
        nChargers: n.n_chargers,
        availability: n.availability,
      })
    }
    for (const e of edges) {
      if (!g.hasNode(e.src) || !g.hasNode(e.dst)) continue
      // mergeEdge — 같은 쌍이 두 번 오면 던지지 않고 덮는다 (무방향 그래프)
      g.mergeEdge(e.src, e.dst, {
        size: widthOf(e.weight),
        color: e.edge_type === 'CORRELATED' ? corrColor : simColor,
        edgeType: e.edge_type,
        weight: e.weight,
        zIndex: 0,
      })
    }
    return g
  }, [nodes, edges])

  useEffect(() => {
    focusRef.current = focus
  }, [focus])

  useEffect(() => {
    const el = stageRef.current
    if (!el) return

    const ink3 = cssVar('--c-ink-3', '#626266')
    const ink2 = cssVar('--c-ink-2', '#444446')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    const divider = cssVar('--c-divider', '#f0f0f0')
    const naColor = cssVar('--c-na', '#6f6f75')
    const accent = cssVar('--c-accent', '#0066cc')
    const dimNode = cssVar('--c-na-soft', '#e9e9ec')
    const parchment = cssVar('--c-parchment', '#f5f5f7')
    /** 한 프레임 안에서 이미 글자를 놓은 자리. 매 렌더 시작에 비운다 */
    const takenLabels: { x0: number; y0: number; x1: number; y1: number }[] = []

    const renderer = new Sigma<NodeAttrs, EdgeAttrs>(graph, el, {
      // 첫 렌더에서 폭이 0 일 수 있다 (카드가 아직 배치되기 전)
      allowInvalidContainer: true,
      labelFont: cssVar('--font-sans', 'sans-serif'),
      labelSize: 11,
      labelWeight: '400',
      labelColor: { color: ink2 },
      // 146 개 라벨을 전부 찍으면 서로 겹쳐 하나도 못 읽는다. 큰 노드만 이름을 얻고
      // 나머지는 hover 와 표에서 확인한다
      labelDensity: 0.4,
      labelRenderedSizeThreshold: 7,
      /**
       * 라벨 그리기를 직접 맡는 이유 둘.
       *
       * 1. **겹치면 뒤엣것을 버린다.** sigma 의 격자 추림은 칸마다 하나를 남기는
       *    방식이라, 30px 밖에 안 떨어진 두 큰 노드가 서로 다른 칸에 들어가면 이름
       *    두 개가 같은 자리에 찍힌다 — 실측에서 "파워큐브"와 "에버온"이 겹쳐 둘 다
       *    못 읽는 글자 뭉치가 됐다. 이름 하나를 잃는 편이 둘 다 잃는 것보다 낫다.
       * 2. **배경색 테두리를 먼저 깐다.** 기본 그리기는 글자만 찍으므로 파란 원이나
       *    엣지 뭉치 위에서 대비가 무너진다.
       */
      defaultDrawNodeLabel: (ctx, data, settings) => {
        if (!data.label) return
        ctx.font = `${settings.labelWeight} ${settings.labelSize}px ${settings.labelFont}`
        const x = data.x + data.size + 4
        const y = data.y + settings.labelSize / 3
        const w = ctx.measureText(data.label).width
        const box = { x0: x - 2, y0: y - settings.labelSize, x1: x + w + 2, y1: y + 4 }
        const clash = takenLabels.some(
          (t) => t.x0 < box.x1 && box.x0 < t.x1 && t.y0 < box.y1 && box.y0 < t.y1,
        )
        if (clash) return
        takenLabels.push(box)
        ctx.lineJoin = 'round'
        ctx.lineWidth = 3
        ctx.strokeStyle = parchment
        ctx.strokeText(data.label, x, y)
        ctx.fillStyle = ink2
        ctx.fillText(data.label, x, y)
      },
      defaultNodeColor: cssVar('--c-cat-1', '#0066cc'),
      defaultEdgeColor: divider,
      zIndex: true,
      minCameraRatio: 0.05,
      maxCameraRatio: 8,
      enableEdgeEvents: false,
      nodeReducer: (node, data) => {
        const h = hoverRef.current
        if (h == null) return data
        if (node === h || nearNodesRef.current.has(node)) return { ...data, zIndex: 2 }
        // 이웃이 아닌 것은 지우지 않고 흐리게 둔다 — 사라지면 "원래 없던 것"이 된다
        return { ...data, color: dimNode, label: null, zIndex: 0 }
      },
      edgeReducer: (edge, data) => {
        const h = hoverRef.current
        if (h == null) return data
        if (nearEdgesRef.current.has(edge)) return { ...data, color: ink3, zIndex: 2 }
        return { ...data, color: divider, zIndex: 0 }
      },
    })

    // 신뢰도 테두리는 WebGL 노드 프로그램이 그리지 못한다 (원 채움만 가능). 파선·점선을
    // 쓰려면 2D 컨텍스트가 필요하므로 sigma 위에 우리 캔버스를 한 장 얹는다.
    // 마우스는 통과시켜야 sigma 의 hover 가 계속 동작한다
    renderer.createCanvasContext('trust', { style: { pointerEvents: 'none' } })
    renderer.resize(true) // 나중에 만든 레이어에 크기를 입힌다
    const canvases = renderer.getCanvases()
    /*
     * **테두리 레이어를 라벨 아래로 내린다.** 나중에 만든 캔버스는 맨 위에 쌓이는데,
     * 그러면 파선·점선 테두리가 이름 글자 위를 가로질러 획을 끊는다 — 실측에서
     * "파워큐브"가 옆 노드들의 대시에 잘려 다른 이름이 겹쳐 찍힌 것처럼 보였다.
     * 테두리는 노드의 일부이므로 노드 위·라벨 아래가 제자리다.
     */
    if (canvases.trust && canvases.labels) {
      canvases.labels.parentNode?.insertBefore(canvases.trust, canvases.labels)
    }
    const trust = canvases.trust?.getContext('2d') ?? null

    const drawTrust = () => {
      if (!trust) return
      const { width, height: h } = renderer.getDimensions()
      trust.clearRect(0, 0, width, h)
      const hoveredId = hoverRef.current
      graph.forEachNode((node, attrs) => {
        const d = renderer.getNodeDisplayData(node)
        if (!d || d.hidden) return
        const p = renderer.framedGraphToViewport(d)
        if (p.x < -40 || p.x > width + 40 || p.y < -40 || p.y > h + 40) return
        const dim = hoveredId != null && node !== hoveredId && !nearNodesRef.current.has(node)
        const radius = renderer.scaleSize(d.size) + 2
        trust.setLineDash(trustDash(attrs.coverage))
        trust.lineWidth = 1.5
        trust.strokeStyle = dim ? hairline : attrs.coverage == null ? naColor : ink3
        trust.beginPath()
        trust.arc(p.x, p.y, radius, 0, TAU)
        trust.stroke()
        if (node === focusRef.current) {
          // 드릴다운으로 들어온 노드 — 어디서 들어왔는지 그림에도 남는다
          trust.setLineDash([])
          trust.lineWidth = 2
          trust.strokeStyle = accent
          trust.beginPath()
          trust.arc(p.x, p.y, radius + 3.5, 0, TAU)
          trust.stroke()
        }
      })
      trust.setLineDash([])
    }

    const repaint = () => {
      renderer.refresh({ skipIndexation: true })
    }

    renderer.on('beforeRender', () => {
      takenLabels.length = 0
    })
    renderer.on('afterRender', drawTrust)

    /*
     * **한 번 강제로 다시 그린다.** sigma 는 생성자 안에서 첫 렌더를 끝내 버리므로
     * 그 렌더는 위 `afterRender` 를 붙이기 전에 지나간다 — 신뢰도 테두리가 첫 화면에
     * 한 번도 안 그려진다. 게다가 우리가 trust 레이어를 얹으며 부른 `resize(true)` 가
     * 라벨 캔버스를 지워, 라벨까지 함께 사라진다. 창 크기를 바꾸거나 노드에 마우스를
     * 올려야 비로소 나타나는데, 그 전까지 화면은 "테두리로 신뢰도를 말한다"는 카드의
     * 설명과 다른 그림을 보여준다. refresh() 로 라벨 격자까지 다시 잡고 한 번 그린다.
     */
    renderer.refresh()

    renderer.on('enterNode', ({ node }) => {
      hoverRef.current = node
      nearNodesRef.current = new Set(graph.neighbors(node))
      nearEdgesRef.current = new Set(graph.edges(node))
      repaint()
      setHovered({
        id: node,
        label: graph.getNodeAttribute(node, 'label'),
        nChargers: graph.getNodeAttribute(node, 'nChargers'),
        availability: graph.getNodeAttribute(node, 'availability'),
        coverage: graph.getNodeAttribute(node, 'coverage'),
        nNeighbors: graph.neighbors(node).length,
      })
    })

    renderer.on('leaveNode', () => {
      hoverRef.current = null
      nearNodesRef.current = new Set()
      nearEdgesRef.current = new Set()
      repaint()
      setHovered(null)
    })

    renderer.on('doubleClickNode', (payload) => {
      // 막지 않으면 sigma 의 기본 동작(확대)이 함께 일어나 드릴다운이 흔들려 보인다
      payload.preventSigmaDefault()
      drillRef.current(payload.node)
    })

    // 캔버스에는 DOM 노드가 없어 e2e 가 "3번 노드"의 화면 좌표를 알 수 없다.
    // 렌더러를 컨테이너에 붙여 테스트가 좌표를 계산할 수 있게 한다
    ;(el as HTMLDivElement & { __sigma?: Sigma<NodeAttrs, EdgeAttrs> }).__sigma = renderer

    return () => {
      hoverRef.current = null
      nearNodesRef.current = new Set()
      nearEdgesRef.current = new Set()
      setHovered(null)
      renderer.kill()
    }
  }, [graph])

  return (
    <div className="gview">
      {/*
       * 신뢰도 테두리 견본은 여기서 그린다 — 카드 범례의 원형 견본은 실선 링 하나뿐이라
       * "파선"·"점선"이라 적힌 자리에도 실선이 찍힌다. 이름과 다른 모양을 보여주면
       * 범례가 차트를 설명하는 것이 아니라 반박한다. 대시 배열은 캔버스가 쓰는
       * `TRUST_MARKS.dash` 를 그대로 써서 견본과 마크가 한 출처에서 나오게 한다.
       */}
      <ul className="gview__key" aria-label="테두리 신뢰도 견본">
        {TRUST_MARKS.map((t) => (
          <li key={t.cls}>
            <svg width="15" height="15" viewBox="0 0 15 15" aria-hidden="true" focusable="false">
              <circle
                cx="7.5"
                cy="7.5"
                r="5.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeDasharray={t.dash.length > 0 ? t.dash.join(' ') : undefined}
              />
            </svg>
            {t.label}
          </li>
        ))}
      </ul>
      <div className="gview__stage" style={{ height }} ref={stageRef} data-testid="graph-canvas" />
      <p className="gview__hover" data-testid="hover-info" aria-live="polite">
        {hovered ? (
          <>
            <b>{hovered.label}</b>
            <span className="gview__hoversep" aria-hidden="true">
              ·
            </span>
            이웃 <b className="num">{int(hovered.nNeighbors)}</b>곳
            <span className="gview__hoversep" aria-hidden="true">
              ·
            </span>
            충전기 <span className="num">{int(hovered.nChargers)}</span>기
            <span className="gview__hoversep" aria-hidden="true">
              ·
            </span>
            가동률 <span className="num">{pct(hovered.availability)}</span>
            <span className="gview__hoversep" aria-hidden="true">
              ·
            </span>
            커버리지 <span className="num">{pct(hovered.coverage, 1)}</span>
            <span className="gview__hoverhint">더블클릭하면 이 노드의 1-hop 그래프로 들어갑니다</span>
          </>
        ) : (
          <span className="gview__hoverhint">
            노드에 마우스를 올리면 이웃만 남고 나머지가 흐려집니다. 더블클릭하면 그 노드의 1-hop
            그래프로 들어가고, 위 브레드크럼으로 돌아옵니다.
          </span>
        )}
      </p>
    </div>
  )
}
